import { useEffect, useMemo, useRef, useState } from 'react';
import { useMachine } from '@xstate/react';
import type { TrialPlan, TrialResult } from '../lib/types';
import { conditionLabel } from '../lib/schedule';
import { createTrialMachine } from '../lib/trialMachine';
import { StimulusField } from './StimulusField';

interface Props {
  plan: TrialPlan;
  sessionId: string;
  deviceKey: string;
  total: number;
  onComplete: (trial: TrialResult) => void;
}

export function TrialRunner({ plan, sessionId, deviceKey, total, onComplete }: Props) {
  const machine = useMemo(() => createTrialMachine(plan), [plan]);
  const [state, send] = useMachine(machine);
  const [progress, setProgress] = useState(0);
  const [arousal, setArousal] = useState(4);
  const [focus, setFocus] = useState(5);
  const [confidence, setConfidence] = useState(5);
  const rafRef = useRef<number | undefined>(undefined);
  const holdRafRef = useRef<number | undefined>(undefined);
  const [holdMs, setHoldMs] = useState(0);
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    setProgress(0);
    setArousal(plan.condition === 'signal' ? 7 : plan.condition === 'calm' ? 3 : 5);
    setFocus(plan.condition === 'flow' ? 7 : 5);
    setConfidence(5);
    setHoldMs(0);
    setHolding(false);
  }, [plan.id, plan.condition]);

  useEffect(() => {
    if (!state.matches('stimulus')) return undefined;
    const start = performance.now();
    send({ type: 'STIMULUS_STARTED', at: start });
    const loop = () => {
      const now = performance.now();
      const p = Math.min(1, (now - start) / plan.plannedMs);
      setProgress(p);
      if (p >= 1) {
        send({ type: 'STIMULUS_DONE', at: now });
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [plan.plannedMs, send, state.value]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!state.matches('reproduce')) return;
      if (event.code === 'Space' && !holding) {
        event.preventDefault();
        startHold();
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space' && holding) {
        event.preventDefault();
        endHold();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('pointerup', endHold);
    window.addEventListener('blur', endHold);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('pointerup', endHold);
      window.removeEventListener('blur', endHold);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value, holding]);

  const startHold = () => {
    if (!state.matches('reproduce') || holding) return;
    const start = performance.now();
    setHolding(true);
    setHoldMs(0);
    send({ type: 'HOLD_STARTED', at: start });
    const tick = () => {
      setHoldMs(performance.now() - start);
      holdRafRef.current = requestAnimationFrame(tick);
    };
    holdRafRef.current = requestAnimationFrame(tick);
  };

  const endHold = () => {
    if (!holding || !state.matches('reproduce')) return;
    if (holdRafRef.current) cancelAnimationFrame(holdRafRef.current);
    const now = performance.now();
    setHolding(false);
    setHoldMs(now - state.context.holdStart);
    send({ type: 'HOLD_ENDED', at: now });
  };

  const submitReport = () => {
    const stimulusMs = Math.max(1, state.context.stimulusEnd - state.context.stimulusStart);
    const reproducedMs = Math.max(1, state.context.reproducedMs);
    const trial: TrialResult = {
      id: `${sessionId}-${plan.id}`,
      sessionId,
      deviceKey,
      condition: plan.condition,
      mode: 'reproduction',
      trialIndex: plan.trialIndex,
      plannedMs: plan.plannedMs,
      stimulusMs,
      reproducedMs,
      warpRatio: reproducedMs / stimulusMs,
      arousal,
      focus,
      confidence,
      flowScore: state.context.flowScore,
      startedAtPerf: state.context.stimulusStart,
      endedAtPerf: state.context.stimulusEnd,
      createdAt: new Date().toISOString(),
      events: state.context.events
    };
    send({ type: 'SUBMIT_REPORT' });
    onComplete(trial);
  };

  return (
    <div className="trial-shell">
      <div className="trial-head">
        <span className="pill">trial {plan.trialIndex}/{total}</span>
        <span className={`condition-tag condition-${plan.condition}`}>{conditionLabel(plan.condition)}</span>
        <span className="mechanic">{plan.mechanic}</span>
      </div>

      {state.matches('prime') && (
        <section className="prime-card">
          <p className="kicker">No clocks. No counting. Use felt duration.</p>
          <h2>{plan.prompt}</h2>
          <p>When the field ends, hold the replay button for exactly as long as that interval felt.</p>
          <button className="primary" onClick={() => send({ type: 'PRIME' })}>Open the field</button>
        </section>
      )}

      {state.matches('stimulus') && (
        <StimulusField plan={plan} running progress={progress} onFlowHit={(score) => send({ type: 'FLOW_HIT', at: performance.now(), value: score })} />
      )}

      {state.matches('reproduce') && (
        <section className="replay-card">
          <p className="kicker">Replay the felt length</p>
          <h2>Hold until the vanished interval feels complete.</h2>
          <button
            className={`hold-button ${holding ? 'holding' : ''}`}
            onPointerDown={startHold}
            onPointerUp={endHold}
            aria-pressed={holding}
          >
            <span>{holding ? 'release when complete' : 'hold to replay'}</span>
            <strong>{Math.min(99, holdMs / 1000).toFixed(1)}s</strong>
          </button>
          <p className="hint">Keyboard: hold Space. The display is feedback only; do not aim for a number.</p>
        </section>
      )}

      {state.matches('report') && (
        <section className="report-card">
          <p className="kicker">One breath of introspection</p>
          <h2>What was your state?</h2>
          <div className="slider-grid">
            <label>
              <span>Arousal</span>
              <input type="range" min="1" max="9" value={arousal} onChange={(e) => setArousal(Number(e.target.value))} />
              <strong>{arousal}/9</strong>
            </label>
            <label>
              <span>Focus absorption</span>
              <input type="range" min="1" max="9" value={focus} onChange={(e) => setFocus(Number(e.target.value))} />
              <strong>{focus}/9</strong>
            </label>
            <label>
              <span>Confidence</span>
              <input type="range" min="1" max="9" value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} />
              <strong>{confidence}/9</strong>
            </label>
          </div>
          <button className="primary" onClick={submitReport}>Seal this second</button>
        </section>
      )}
    </div>
  );
}
